/**
 * @file Helper functions for unit conversion
 */

/**
 * @typedef {Object} UnitDef
 * @property {function} convert
 * @property {string} translation
 * @property {number} factor
 * @property {number} [exponent]
 * @property {number} [offset] - An offset to apply (relative to the base scale) when converting between units
 *                              (e.g. 273.15 for Celsius on Kelvin scale)
 */

/**
 * Converts between related units.
 *
 * All unit conversions are calculated based on the following equation:
 * @example base unit = (factor * other unit)**exponent
 * The default value for the exponent is +1. A value of e.g. -1 indicated a switched numerator and denominator.
 *
 * Using this equation, basic unit conversions (e.g. kilometres to meters) can be derived (with exponent = 1):
 * Units from quotients (e.g. fuel efficiencies ml/m to m/l) can be derived using the exponent = -1:
 *
 * The resulting conversion equation is:
 * @example valueTo = valueFrom**(exponentFrom * exponentTo) * (factorTo / factorFrom)**(exponentTo)
 *
 * @private
 * @param {UnitDef} unitFrom source unit
 * @param {UnitDef} unitTo target unit
 * @param {number} value the source value to be converted
 * @returns {number} the converted value
 */
function convert(unitFrom, unitTo, value) {
    value = Number(value)

    const factor = unitTo.factor / unitFrom.factor

    // handling exponents separately (although the equation also works without exponent) to simplify calculation
    if (unitFrom.exponent || unitTo.exponent) {
        const toExponent = unitTo.exponent || 1
        return Math.pow(value, (unitFrom.exponent || 1) * toExponent) * Math.pow(factor, toExponent)
    }

    return (value + (unitFrom.offset || 0)) * factor - (unitTo.offset || 0)
}

/**
 * Adds and binds the convert function as a member to each unit definition
 *
 * @private
 * @param {Object} unit the unit, which contains unit definitions
 * @returns {Object} the final unit definitions
 */
function bindConvert(unit) {
    Object.keys(unit).forEach(unitKey => {
        unit[unitKey].convert = convert.bind(null, unit[unitKey])
    })
    return unit
}

/**
 * @type {Object}
 * @property {UnitDef} m
 * @property {UnitDef} km
 * @property {UnitDef} ft
 * @property {UnitDef} yd
 * @property {UnitDef} mi
 */
export const DistUnit = bindConvert({
    m: {
        factor: 1.0,
        translation: 'wf_distunit_m'
    },
    km: {
        factor: 1.0 / 1000.0,
        translation: 'wf_distunit_km'
    },
    ft: {
        factor: 1.0 / 0.304803706,
        translation: 'wf_distunit_ft'
    },
    yd: {
        factor: 1.0 / 0.9144,
        translation: 'wf_distunit_yd'
    },
    mi: {
        factor: 1.0 / (1.609344 * 1000.0),
        translation: 'wf_distunit_mi'
    }
})

/**
 * @type {Object}
 * @property {UnitDef} kmh
 * @property {UnitDef} mph
 * @property {UnitDef} ms
 */
export const SpeedUnit = bindConvert({
    kmh: {
        factor: 1.0,
        translation: 'wf_kmh'
    },
    mph: {
        factor: 1.0 / 1.609344,
        translation: 'wf_mph'
    },
    ms: {
        factor: 1.0 / 3.6,
        translation: 'wf_ms'
    }
})

/**
 * @type {Object}
 * @property {UnitDef} kg
 * @property {UnitDef} t
 * @property {UnitDef} lt - long ton (imperial)
 * @property {UnitDef} st - short ton (US)
 * @property {UnitDef} lbs
 */
export const WeightUnit = bindConvert({
    kg: {
        factor: 1.0,
        translation: 'wf_unit_kg'
    },
    t: {
        factor: 1.0 / 1000,
        translation: 'wf_unit_t'
    },
    lt: {
        factor: 1.0 / 1016.0469088,
        translation: 'wf_unit_lt'
    },
    st: {
        factor: 1.0 / 907.18474,
        translation: 'wf_unit_st'
    },
    lbs: {
        factor: 1.0 / 0.4535922921969,
        translation: 'wf_unit_lbs'
    }
})

/**
 * @type {Object}
 * @property {UnitDef} l
 * @property {UnitDef} galImp
 * @property {UnitDef} galUs
 */
export const LiquidUnit = bindConvert({
    l: {
        factor: 1.0,
        translation: 'wf_litre_short'
    },
    galImp: {
        factor: 1.0 / 4.54609,
        translation: 'wf_gal_short'
    },
    galUs: {
        factor: 1.0 / 3.78541178,
        translation: 'wf_gal_short'
    }
})

/**
 * @type {Object}
 * @property {UnitDef} mlPerM
 * @property {UnitDef} lPer100km
 * @property {UnitDef} kmPerL
 * @property {UnitDef} mpgUk
 * @property {UnitDef} mpgUs
 */
export const FuelEfficiencyUnit = bindConvert({
    mlPerM: {
        factor: 1.0,
        translation: 'wf_unit_fuelefficiency_mlperm_short'
    },
    lPer100km: {
        factor: 100.0,
        translation: 'wf_unit_fuelefficiency_lper100km_short'
    },
    kmPerL: {
        factor: 1.0,
        exponent: -1,
        translation: 'wf_unit_fuelefficiency_kmperl_short'
    },
    mpgUk: {
        factor: 0.35400619,
        exponent: -1,
        translation: 'wf_unit_fuelefficiency_mpguk_short'
    },
    mpgUs: {
        factor: 0.425143707,
        exponent: -1,
        translation: 'wf_unit_fuelefficiency_mpgus_short'
    },
    kpgUs: {
        factor: 0.264172,
        exponent: -1,
        translation: 'wf_unit_fuelefficiency_kpgus_short'
    }
})

export const GaseousFuelEfficiencyUnit = bindConvert({
    gPerM: {
        factor: 1.0
    },
    kgPer100km: {
        factor: 100.0,
        translation: 'wf_unit_fuelefficiency_kgper100km_short'
    }
})

/**
 * @type {Object}
 * @property {UnitDef} kWh
 */
export const EnergyUnit = bindConvert({
    kWh: {
        factor: 1.0,
        translation: 'wf_unit_energy_kwh_short'
    },
    wh: {
        factor: 1000,
        translation: 'wf_unit_energy_wh_short'
    }
})

/**
 * @type {Object}
 * @property {UnitDef} kWhPer100km
 * @property {UnitDef} kWhPer100mi
 * @property {UnitDef} kmPerL
 * @property {UnitDef} kmPerKWh
 * @property {UnitDef} miPerKWh
 */
export const EnergyEfficiencyUnit = bindConvert({
    kWhPer100km: {
        factor: 1.0,
        translation: 'wf_unit_energyefficiency_kwhper100km_short'
    },
    kWhPer100mi: {
        factor: 1.609344,
        translation: 'wf_unit_energyefficiency_kwhper100mi_short'
    },
    kmPerKWh: {
        factor: 0.01,
        exponent: -1,
        translation: 'wf_unit_energyefficiency_kmperkwh_short'
    },
    miPerKWh: {
        factor: 0.016093,
        exponent: -1,
        translation: 'wf_unit_energyefficiency_miperkwh_short'
    }
})

/**
 * @type {Object}
 * @property {UnitDef} rpm
 */
export const RpmUnit = bindConvert({
    rpm: {
        factor: 1.0,
        translation: 'wf_rpm'
    }
})

/**
 * @type {Object}
 * @property {UnitDef} Celsius [°C]
 * @property {UnitDef} Fahrenheit [°F]
 * @property {UnitDef} Kelvin [K]
 */
export const TemperatureUnit = bindConvert({
    Celsius: {
        translation: 'ui_generic-unit_celsius',
        factor: 1.0,
        offset: 273.15
    },
    Fahrenheit: {
        translation: 'ui_generic-unit_fahrenheit',
        factor: 1.8,
        offset: 459.67
    },
    Kelvin: {
        translation: 'ui_generic-unit_kelvin',
        factor: 1.0,
        offset: 0
    }
})

/**
 * @type {Object}
 * @property {UnitDef} bar
 * @property {UnitDef} psi
 */
export const PressureUnit = bindConvert({
    bar: {
        translation: 'wf_unit_bar',
        factor: 1.0
    },
    psi: {
        translation: 'wf_unit_psi',
        factor: 14.5037738
    }
})

/**
 * @type {Object}
 * @property {UnitDef} mW
 * @property {UnitDef} W
 * @property {UnitDef} kW
 * @property {UnitDef} MW
 * @property {UnitDef} GW
 */
export const PowerUnit = bindConvert({
    mW: {
        factor: 1_000_000,
        translation: 'wf_unit_power_mW'
    },
    W: {
        factor: 1_000,
        translation: 'wf_unit_power_W'
    },
    kW: {
        factor: 1.0,
        translation: 'wf_unit_power_kW'
    },
    MW: {
        factor: 1 / 1_000,
        translation: 'wf_unit_power_MW'
    },
    GW: {
        factor: 1 / 1_000_000,
        translation: 'wf_unit_power_GW'
    }
})
